import { IOption } from 'components/_form/NestedSelect/NestedSelect';
import { AreaAccordionItem, IArea } from 'types/area';
import { displayTranslatedField } from './localizedString';

export const parentToChildrenAreasStructure = (dataToRestructure: IArea[]) => {
  const data = structuredClone(dataToRestructure);
  const idMap = new Map(); // Map to store ids and their corresponding objects

  // First loop to create idMap
  for (let i = 0; i < data.length; i++) {
    const obj = data[i];
    const id = obj.id;
    idMap.set(id, obj);
  }

  // Second loop to set parent references
  for (let i = 0; i < data.length; i++) {
    const obj = data[i];
    const parentId = obj.parent_id ?? null;
    if (parentId) {
      const parentObj = idMap.get(parentId);
      if (parentObj) {
        if (!parentObj.children) {
          parentObj.children = []; // Create children array if not present
        }
        parentObj.children.push(obj); // Add current object as child of parent object
      }
    }
  }

  // Filter out objects that were originally children (i.e., have parent references)
  const result = data.filter((obj) => !obj.parent_id);

  return result;
};

/**
 * Returns ordered ancestor names for a given area, e.g. ["Core", "desc1", "its"].
 * Parses the ancestry string ("/1/3/7/") and maps IDs to names from the flat list.
 * No extra requests — reuses data already fetched by getAreas().
 */
export const getAreaBreadcrumb = (area: IArea, allAreas: IArea[]): string[] => {
  if (!area.ancestry || area.ancestry === '/') return [area.name];

  const idToName = new Map(allAreas.map((a) => [a.id, a.name]));
  const ancestorIds = area.ancestry
    .split('/')
    .filter(Boolean)
    .map(Number);

  return [...ancestorIds.map((id) => idToName.get(id) ?? String(id)), area.name];
};

/** "Parent > Child > Area" using translated names (fallback: base name). */
export const getAreaMaterializedPathLabel = (
  area: IArea,
  allAreas: IArea[],
  separator = ' > '
): string => {
  const idToName = new Map(
    allAreas.map((a) => [a.id, displayTranslatedField(a, 'name')])
  );

  const ancestorIds = (area.ancestry ?? '')
    .split('/')
    .filter(Boolean)
    .map(Number);

  const names = [
    ...ancestorIds.map((id) => idToName.get(id) ?? String(id)),
    displayTranslatedField(area, 'name')
  ].filter(Boolean);

  return names.join(separator);
};

export const parseParentToChildrenAreasStructureToOptions = (
  items: AreaAccordionItem[]
): IOption[] => {
  return items.map((item) => ({
    label: displayTranslatedField(item, 'name'),
    selectedLabel: item.materialized_path_label ?? displayTranslatedField(item, 'name'),
    value: String(item.id),
    isDisabled: item.isDisabled,
    ...(item.children?.length
      ? {
        options: parseParentToChildrenAreasStructureToOptions(item.children)
      }
      : {})
  }));
};
